import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore/lite';

import commentConverter from './commentConverter';
import CommentData from '../types/commentData';
import { firestore } from '../utils/firebase';

export type NewCommentData = Omit<CommentData, 'id' | 'upUuids' | 'downUuids' | 'replyIds' | 'created'>;

export default class CommentApi {
  private static readonly collectionReference = collection(firestore, 'comments').withConverter(commentConverter);

  public static async addComment(comment: NewCommentData): Promise<string> {
    const docRef = doc(this.collectionReference);
    const id = docRef.id;
    const positiveRatingUuids: string[] = [];
    const negativeRatingUuids: string[] = [];
    await setDoc(docRef, {
      ...comment,
      id,
      upUuids: positiveRatingUuids,
      downUuids: negativeRatingUuids,
      created: serverTimestamp(),
    });
    return id;
  }

  public static async getComment(id: string): Promise<CommentData> {
    const docRef = doc(this.collectionReference, id);
    const docSnap = await getDoc(docRef);
    return docSnap.data()!;
  }

  public static async getComments(): Promise<CommentData[]> {
    const collection = await getDocs(this.collectionReference);
    return collection.docs.map(doc => doc.data() as CommentData);
  }

  public static async getCommentsByContextId(contextId: string): Promise<CommentData[]> {
    const querySnaps = await getDocs(
      query(this.collectionReference, where('contextId', '==', contextId), orderBy('created', 'asc'))
    );
    return querySnaps.docs.map(docSnap => docSnap.data() as CommentData);
  }

  public static async updateComment(comment: CommentData) {
    return updateDoc(doc(this.collectionReference, comment.id), { ...comment });
  }

  public static async removeComment(commentId: string) {
    return await deleteDoc(doc(firestore, 'comments', commentId));
  }
}
